import React from "react";
import { CalendarDaysIcon, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { addMonths, eachDayOfInterval, endOfMonth, endOfWeek, format, isSameDay, isSameMonth, startOfMonth, startOfWeek, subMonths } from "date-fns";
import { filterDaySummaryByUser, getDaySummary } from "./calendarData";
type PerfectDayCalendarProps = {
  month: Date;
  selectedDate: Date | null;
  selectedUserId: string | null;
  selectedUserName?: string;
  onMonthChange: (month: Date) => void;
  onSelectDate: (date: Date) => void;
};
export function PerfectDayCalendar({
  month,
  selectedDate,
  selectedUserId,
  selectedUserName,
  onMonthChange,
  onSelectDate
}: PerfectDayCalendarProps) {
  const calendarDays = eachDayOfInterval({
    start: startOfWeek(startOfMonth(month), {
      weekStartsOn: 1
    }),
    end: endOfWeek(endOfMonth(month), {
      weekStartsOn: 1
    })
  });
  const monthDays = calendarDays.filter(date => isSameMonth(date, month));
  const getScopedSummary = (date: Date) => filterDaySummaryByUser(getDaySummary(date), selectedUserId);
  const totalPerfectDays = monthDays.reduce((total, date) => total + getScopedSummary(date).perfectCount, 0);
  const totalPossibleDays = monthDays.reduce((total, date) => total + getScopedSummary(date).totalUsers, 0);
  const monthCompletion = Math.round(totalPerfectDays / totalPossibleDays * 100);
  const scopeLabel = selectedUserName ? `${selectedUserName}'s` : "Team";
  return <section aria-label="Perfect day calendar" className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_18px_50px_-30px_rgba(15,23,42,0.28)]" data-id="element-92" data-mp="34nydy5zpaczu">
      <div className="border-b border-slate-100 bg-slate-50/80 px-4 py-5 sm:px-6 sm:py-6" data-id="element-93" data-mp="3edhxzez5gcwa">
        <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-94" data-mp="39lrh0q5piqvw">
          <div className="flex items-start gap-3" data-id="element-95" data-mp="39kgcgo4ftf3q">
            <span className="grid h-10 w-10 place-items-center rounded-2xl bg-teal-600 text-white shadow-sm" data-id="element-96" data-mp="2lvdyprcxc6ei"><CalendarDaysIcon size={19} data-id="element-97" data-mp="2gwkme29dilfx" /></span>
            <div data-id="element-98" data-mp="2gvrira5poc20">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-teal-700" data-id="element-99" data-mp="kupg6b80piv4">{scopeLabel} consistency</p>
              <h2 className="mt-1 text-2xl font-semibold tracking-tight text-slate-950" data-id="element-100" data-mp="psr00yd9pm7n">{format(month, "MMMM yyyy")}</h2>
              <p className="mt-1 text-sm text-slate-500" data-id="element-101" data-mp="ulk4rm8qkujq">Select a day to review the check-in details.</p>
            </div>
          </div>
          <div className="flex items-center gap-2" data-id="element-102" data-mp="ynow2ebppf37">
            <button type="button" aria-label="Previous month" onClick={() => onMonthChange(subMonths(month, 1))} className="grid h-10 w-10 place-items-center rounded-xl border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:-translate-y-0.5 hover:border-teal-300 hover:text-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500" data-id="element-103" data-mp="1wmxee62ci5r">
              <ChevronLeftIcon size={18} data-id="element-104" data-mp="1mbgi49n216ho" />
            </button>
            <button type="button" aria-label="Next month" onClick={() => onMonthChange(addMonths(month, 1))} className="grid h-10 w-10 place-items-center rounded-xl border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:-translate-y-0.5 hover:border-teal-300 hover:text-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500" data-id="element-105" data-mp="1mb6ugsphfbf5">
              <ChevronRightIcon size={18} data-id="element-106" data-mp="1ro54ajm301r4" />
            </button>
          </div>
        </div>

        <div className="mt-5 flex items-center gap-3 rounded-2xl border border-teal-100 bg-white p-3 shadow-sm sm:ml-[52px] sm:max-w-sm" data-id="element-107" data-mp="1nnlb1r4mpia9">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-teal-50 text-sm font-bold text-teal-700" data-id="element-108" data-mp="1ipjrz677diys">{monthCompletion}%</div>
          <div data-id="element-109" data-mp="18pj55aa4kkpu">
            <p className="text-sm font-semibold text-slate-800" data-id="element-110" data-mp="18p9gpr4qalm9">{scopeLabel} monthly completion</p>
            <p className="text-xs text-slate-500" data-id="element-111" data-mp="14opkcprzbm16">{totalPerfectDays} perfect days across {monthDays.length} calendar days</p>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6" data-id="element-112" data-mp="331hdla0m1cko">
        <div className="mx-auto max-w-3xl" data-id="element-113" data-mp="2z1yu8ioft9jy">
          <div className="mb-3 grid grid-cols-7 gap-2 text-center sm:mb-4 sm:gap-3" data-id="element-114" data-mp="2z303qipt1nzk">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(day => <div key={day} className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400 sm:text-xs" data-id="element-115" data-mp="3b3gototd6cdp">{day}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-2 sm:gap-3" data-id="element-116" data-mp="316bz5f0b7ys5">
            {calendarDays.map(date => {
            const isCurrentMonth = isSameMonth(date, month);
            const isSelected = selectedDate ? isSameDay(date, selectedDate) : false;
            const summary = getScopedSummary(date);
            const percentage = summary.totalUsers ? Math.round(summary.perfectCount / summary.totalUsers * 100) : 0;
            const tileClasses = getTileClasses(percentage, isCurrentMonth);
            return <button type="button" key={date.toISOString()} onClick={() => onSelectDate(date)} aria-pressed={isSelected} aria-label={`${format(date, "MMMM d")}: ${summary.perfectCount} of ${summary.totalUsers} users had a perfect day`} className={`group aspect-square min-w-0 rounded-xl border-2 transition duration-200 focus:z-10 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 sm:rounded-2xl ${tileClasses} ${isSelected ? "z-[1] border-teal-700 shadow-[0_0_0_3px_rgba(20,184,166,0.18)]" : "hover:-translate-y-0.5 hover:shadow-md"}`} data-id="element-117" data-mp="24ft8a2qlk9v0">
                  <span className="flex h-full flex-col items-center justify-center gap-1 px-1 sm:gap-1.5" data-id="element-118" data-mp="2ihs1ghjfboc7">
                    <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400 sm:text-xs" data-id="element-119" data-mp="2dif3b0n3quub">{format(date, "d")}</span>
                    <span className={`text-base font-bold leading-none tracking-tight sm:text-xl ${isCurrentMonth ? "text-slate-950" : "text-slate-300"}`} data-id="element-120" data-mp="2rjholb69w146">{summary.perfectCount}/{summary.totalUsers}</span>
                  </span>
                </button>;
          })}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-slate-100 px-5 py-4 text-xs text-slate-600 sm:px-6" data-id="element-121" data-mp="1p37gd69y5i6g">
        <span className="font-semibold text-slate-700" data-id="element-122" data-mp="1xxdj5i29npew">Completion health</span>
        <span className="inline-flex items-center gap-2" data-id="element-123" data-mp="1xzgd2d6ji2ow"><i className="h-2 w-2 rounded-full bg-teal-500" data-id="element-124" data-mp="22vneptrwks11" />75%+ strong</span>
        <span className="inline-flex items-center gap-2" data-id="element-125" data-mp="27va04qhvxekx"><i className="h-2 w-2 rounded-full bg-teal-300" data-id="element-126" data-mp="27tyn002juuh5" />50–74% steady</span>
        <span className="inline-flex items-center gap-2" data-id="element-127" data-mp="2cw7j35apycnh"><i className="h-2 w-2 rounded-full bg-slate-200" data-id="element-128" data-mp="2gwhmqbb1gqz6" />Below 50% needs attention</span>
      </div>
    </section>;
}
function getTileClasses(percentage: number, isCurrentMonth: boolean): string {
  if (!isCurrentMonth) {
    return "border-slate-100 bg-slate-50 text-slate-300 opacity-60";
  }
  if (percentage >= 75) {
    return "border-teal-300 bg-white text-slate-700";
  }
  if (percentage >= 50) {
    return "border-amber-300 bg-white text-slate-700";
  }
  return "border-rose-200 bg-white text-slate-700";
}